import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonItem,
    IonRadioGroup,
    IonRadio,
    IonModal,
} from "@ionic/react";


interface QuestionData {
    word: string;
    correctAnswer: string;
    options: string[]; // Type of noun: Concrete or Abstract
}


const OppositeWords: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const modal = useRef<HTMLIonModalElement>(null);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleSubmit = (option: React.SetStateAction<string | null>) => {
        setSelectedOption(option);
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctAnswer) {
            // Automatically move to the next question
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                }
                else {
                    setCurrentIndex(0);
                    fetchQuestions();
                }
            }, 5000); // Delay reduced to 3 seconds
        }
    };

    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'OppositeWords.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/OppositeWords.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Opposites Worksheets for UKG | Learn Antonyms with Fun Activities"; // Set page title
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content='Explore fun and interactive opposite word activities for UKG kids at Mama’s Guidance to enhance vocabulary and understanding of word meanings through engaging exercises.' />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Opposites</h1>

                                <p>
                                    Teaching <strong>opposites</strong> (also known as <strong>antonyms</strong>) is a simple and effective way to expand your child’s vocabulary. Understanding opposite words helps UKG children build strong language skills and improve their comprehension. These worksheets include a variety of activities using words, pictures, and matching games.
                                </p>

                                <h2>🔄 What Are Opposites?</h2>
                                <p>
                                    Opposites are words with completely different or contrasting meanings. For example:
                                </p>
                                <ul>
                                    <li>Big – Small</li>
                                    <li>Hot – Cold</li>
                                    <li>Happy – Sad</li>
                                    <li>Up – Down</li>
                                    <li>Fast – Slow</li>
                                </ul>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize common pairs of opposite words</li>
                                    <li>Understand meanings through visuals</li>
                                    <li>Match opposite words and pictures</li>
                                    <li>Use opposites correctly in sentences</li>
                                </ul>

                                <h2>🖍️ Activities in the Worksheets</h2>
                                <ul>
                                    <li>Match the word with its opposite</li>
                                    <li>Circle the correct opposite from options</li>
                                    <li>Picture-based opposites identification</li>
                                    <li>Fill in the blank with the correct antonym</li>
                                    <li>Opposites coloring and tracing</li>
                                </ul>

                                <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                                <ul>
                                    <li>Use daily examples like <strong>“open/close” or “day/night”</strong></li>
                                    <li>Make it a fun game: <strong>“What’s the opposite of ___?”</strong></li>
                                    <li>Use flashcards with visuals and words</li>
                                    <li>Encourage children to speak in full sentences using both words</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/english-language/articles"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Articles</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/one-and-many"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>One and Many</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/english-language/rhyming-words"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Rhyming Words</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Play <strong>“opposites hunt”</strong> at home – ask your child to find objects that are opposite like <strong>light/heavy, clean/dirty, or wet/dry</strong>. Learning through fun sticks longer!
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Opposites</h1>

                            <p>
                                Teaching <strong>opposites</strong> (also known as <strong>antonyms</strong>) is a simple and effective way to expand your child’s vocabulary. Understanding opposite words helps UKG children build strong language skills and improve their comprehension. These worksheets include a variety of activities using words, pictures, and matching games.
                            </p>

                            <h2>🔄 What Are Opposites?</h2>
                            <p>
                                Opposites are words with completely different or contrasting meanings. For example:
                            </p>
                            <ul>
                                <li>Big – Small</li>
                                <li>Hot – Cold</li>
                                <li>Happy – Sad</li>
                                <li>Up – Down</li>
                                <li>Fast – Slow</li>
                            </ul>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Recognize common pairs of opposite words</li>
                                <li>Understand meanings through visuals</li>
                                <li>Match opposite words and pictures</li>
                                <li>Use opposites correctly in sentences</li>
                            </ul>

                            <h2>🖍️ Activities in the Worksheets</h2>
                            <ul>
                                <li>Match the word with its opposite</li>
                                <li>Circle the correct opposite from options</li>
                                <li>Picture-based opposites identification</li>
                                <li>Fill in the blank with the correct antonym</li>
                                <li>Opposites coloring and tracing</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>Use daily examples like <strong>“open/close” or “day/night”</strong></li>
                                <li>Make it a fun game: <strong>“What’s the opposite of ___?”</strong></li>
                                <li>Use flashcards with visuals and words</li>
                                <li>Encourage children to speak in full sentences using both words</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/english-language/articles"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Articles</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/one-and-many"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>One and Many</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/english-language/rhyming-words"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Rhyming Words</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Play <strong>“opposites hunt”</strong> at home – ask your child to find objects that are opposite like <strong>light/heavy, clean/dirty, or wet/dry</strong>. Learning through fun sticks longer!
                            </p>
                        </article>
                        <h3>{questions[currentIndex]?.word}</h3>

                        <IonRadioGroup
                            value={selectedOption}
                            onIonChange={(e) => handleSubmit(e.detail.value)}
                        >
                            {questions[currentIndex]?.options.map((option, index) => (
                                <IonItem
                                    className={` ${selectedOption === option
                                        ? option === questions[currentIndex]?.correctAnswer
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}
                                    key={index}>
                                    <IonLabel>{option}</IonLabel>
                                    <IonRadio slot="start" value={option} />
                                </IonItem>
                            ))}
                        </IonRadioGroup>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default OppositeWords;
